import { useEffect, useState } from 'react';
import { Copy, Link2, ExternalLink } from 'lucide-react';
import { useNegocioStore } from '@renderer/store/useNegocioStore';
import { centavosAPesos, pesosACentavos } from '@shared/centavos';
import { copiarAlPortapapeles } from '@renderer/lib/portapapeles';
import { esWeb } from '@renderer/lib/entorno';

/**
 * Sprint 92 — El enlace del menú que el negocio comparte por WhatsApp.
 *
 * Dos decisiones, y las dos son del negocio, no del código:
 *
 * 1. **Encenderlo.** Es una puerta a internet que escribe en su base. Nace apagada a propósito: en
 *    este proyecto ya nos costó una vez que un booleano con default abriera algo al público
 *    (`onboardingCompletado`, cuyo paso 4 crea un dueño).
 * 2. **La compra mínima.** Por WhatsApp el negocio filtra solo —ve el pedido de $40 y decide si
 *    manda la moto—; un enlace abierto no puede. El monto **no está en el código**: cambia con el
 *    precio de la gasolina, y quien lo sabe es quien paga los envíos.
 */
export function MenuPublicoForm() {
  const negocio = useNegocioStore((s) => s.negocio);
  const cargarNegocio = useNegocioStore((s) => s.cargar);

  const [activo, setActivo] = useState(false);
  const [minimo, setMinimo] = useState('0');
  /** Sprint 96 — Los dos ajustes que nacieron de ver el enlace en el aire. */
  const [maxSinConfirmar, setMaxSinConfirmar] = useState('2');
  const [centavosFolio, setCentavosFolio] = useState(true);
  /**
   * Sprint 137 — ¿El cliente puede elegir "pago al recibir" desde el enlace?
   *
   * Nace apagado y se enciende aquí. Encenderlo **no** manda nada a la parrilla: el pedido del
   * enlace sigue cayendo en "Llegaron del enlace" para que alguien lo revise. Lo único que
   * cambia es que el cliente ya dice cómo quiere pagar, en vez de tener que preguntárselo.
   */
  const [contraEntrega, setContraEntrega] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [copiado, setCopiado] = useState(false);

  useEffect(() => {
    if (!negocio) return;
    setActivo(negocio.menuPublicoActivo ?? false);
    setMinimo(String(centavosAPesos(negocio.compraMinima ?? 0)));
    setMaxSinConfirmar(String(negocio.maxPedidosSinConfirmar ?? 2));
    setCentavosFolio(negocio.centavosDeFolio !== false);
    setContraEntrega(negocio.contraEntregaEnEnlace === true);
  }, [negocio]);

  const num = (t: string) => {
    const n = Number((t ?? '').replace(',', '.'));
    return Number.isFinite(n) && n >= 0 ? n : 0;
  };

  const guardar = async () => {
    setGuardando(true);
    const r = await window.api.negocio.actualizar({
      menuPublicoActivo: activo,
      compraMinima: pesosACentavos(num(minimo)),
      maxPedidosSinConfirmar: Math.max(0, Math.round(num(maxSinConfirmar))),
      centavosDeFolio: centavosFolio,
      contraEntregaEnEnlace: contraEntrega,
    });
    setGuardando(false);
    if (r.ok) {
      await cargarNegocio();
      await window.alertar({
        titulo: activo ? 'Menú en línea encendido' : 'Menú en línea apagado',
        mensaje: activo
          ? 'Ya puedes compartir tu enlace con los clientes.'
          : 'El enlace deja de funcionar hasta que lo vuelvas a encender.',
        variant: 'success',
      });
    }
  };

  /**
   * El enlace real depende de dónde esté publicado el sistema, así que se arma del navegador. En
   * el escritorio no hay servidor que lo atienda: mostrarlo ahí sería prometer algo que no existe.
   */
  const enlace = esWeb() ? `${window.location.origin}/menu` : null;

  const copiar = async () => {
    if (!enlace) return;
    setCopiado(await copiarAlPortapapeles(enlace));
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-bold">Menú en línea</h3>
        <p className="text-sm text-slate-500">
          Un enlace que le mandas al cliente por WhatsApp para que arme su pedido solo.
        </p>
      </div>

      <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
        <input
          type="checkbox"
          className="mt-1 h-5 w-5"
          checked={activo}
          onChange={(e) => setActivo(e.target.checked)}
        />
        <span className="text-sm">
          <span className="block font-semibold">Encender el menú en línea</span>
          <span className="mt-0.5 block text-xs text-slate-600">
            Mientras esté apagado, el enlace no abre nada. Los pedidos que lleguen por aquí caen en{' '}
            <b>“llegaron del enlace”</b> y no van a la cocina hasta que tú los confirmes.
          </span>
        </span>
      </label>

      <div className="max-w-xs">
        <label className="label" htmlFor="compraMinima">
          Compra mínima a domicilio
        </label>
        <input
          id="compraMinima"
          className="input"
          inputMode="decimal"
          value={minimo}
          onChange={(e) => setMinimo(e.target.value)}
        />
        <p className="mt-1 text-xs text-slate-500">
          Déjalo en <b>0</b> si no quieres mínimo. Por teléfono tú decides caso por caso; el enlace
          no puede, así que aquí es donde se pone la regla.
        </p>
      </div>

      {activo && enlace && (
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-blue-900">
            <Link2 className="h-4 w-4" />
            Tu enlace
          </div>
          <div className="mt-1 break-all font-mono text-sm text-blue-800">{enlace}</div>
          <div className="mt-3 flex flex-wrap gap-2">
            <button onClick={copiar} className="btn-primary min-h-[44px] text-sm">
              <Copy className="h-4 w-4" />
              {copiado ? '✓ Copiado' : 'Copiar enlace'}
            </button>
            <a
              href={enlace}
              target="_blank"
              rel="noreferrer"
              className="btn-ghost min-h-[44px] border border-slate-300 text-sm"
            >
              <ExternalLink className="h-4 w-4" />
              Ver cómo lo ven
            </a>
          </div>
          <p className="mt-2 text-xs text-blue-800">
            Pégalo en tu chat, o déjalo en tu estado de WhatsApp para que lo tengan a la mano.
          </p>
        </div>
      )}

      {activo && !enlace && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
          El enlace sólo funciona en la versión web del sistema. Aquí puedes dejarlo configurado.
        </p>
      )}

      {/*
        Sprint 96 — Los dos ajustes que salieron de ver el enlace funcionando.
      */}
      <div className="grid grid-cols-1 gap-4 border-t border-slate-200 pt-4 sm:grid-cols-2">
        <div>
          <label className="label" htmlFor="maxSinConfirmar">
            Pedidos sin confirmar por teléfono
          </label>
          <input
            id="maxSinConfirmar"
            className="input max-w-[110px]"
            inputMode="numeric"
            value={maxSinConfirmar}
            onChange={(e) => setMaxSinConfirmar(e.target.value)}
          />
          <p className="mt-1 text-xs text-slate-500">
            Si alguien ya te mandó ese número de pedidos y no le has confirmado ninguno, no puede
            mandar otro. Es lo que evita que te llenen el tablero de pedidos falsos. <b>0</b> = sin
            tope.
          </p>
        </div>

        <label className="flex cursor-pointer items-start gap-3">
          <input
            type="checkbox"
            className="mt-1 h-5 w-5"
            checked={centavosFolio}
            onChange={(e) => setCentavosFolio(e.target.checked)}
          />
          <span className="text-sm">
            <span className="block font-semibold">Cobrar con los centavos del pedido</span>
            <span className="mt-0.5 block text-xs text-slate-600">
              El pedido <b>#123</b> pide <b>$580.23</b>. Cuando te llegue la notificación del banco
              por ese monto, <b>ya sabes de quién es</b> sin preguntarle a nadie. Nunca se cobra de
              menos: el ajuste es de unos centavos hacia arriba.
            </span>
          </span>
        </label>

        {/*
          Sprint 137 — Dejar que el cliente pida "pago al recibir" desde el enlace.

          Apagado de fábrica a propósito. Un pedido falso por el enlace hoy no cuesta nada —nadie
          lo cocina hasta que una persona lo revisa—; con esto encendido tampoco se cocina solo,
          pero el negocio empieza a recibir peticiones de crédito de gente que no conoce. Que sea
          una decisión y no un default.
        */}
        <label className="flex cursor-pointer items-start gap-3">
          <input
            type="checkbox"
            className="mt-1 h-5 w-5"
            checked={contraEntrega}
            onChange={(e) => setContraEntrega(e.target.checked)}
          />
          <span className="text-sm">
            <span className="block font-semibold">Dejar que elijan pagar al recibir</span>
            <span className="mt-0.5 block text-xs text-slate-600">
              En tu enlace aparece la opción <b>“pago al recibir”</b>. Aunque la elijan,{' '}
              <b>el pedido no se cocina solo</b>: te sigue llegando para que lo revises y decidas
              tú. Apagado, tus clientes sólo pueden pagar por transferencia.
            </span>
          </span>
        </label>
      </div>

      <button onClick={guardar} className="btn-primary min-h-[44px]" disabled={guardando}>
        {guardando ? 'Guardando…' : 'Guardar'}
      </button>
    </div>
  );
}
